import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Card, Flex, Tabs, Typography } from 'antd';

import { v4 as uuidv4 } from 'uuid';

import { hasPermission } from '../../../helpers/user';

import { DEFAULT_TAB, MAPS_TAB } from '../../../constants/map';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { PLAN_TYPE } from '../../../constants/subscription';

import { useQuery } from '../../../hooks/useQuery';

import { fetchAnimalsTags } from '../../../redux/actions/animal';
import { fetchFarms } from '../../../redux/actions/farm';
import { fetchGrazingData, fetchGrazingHeatmapStyles } from '../../../redux/actions/grazing';
import { setSelectedIds, setTimeSlidersData, setTimeSlidersVisibility } from '../../../redux/reducers/animal';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setMapData } from '../../../redux/reducers/map';

import LoadingBar from '../../../components/LoadingBar';
import Map from '../../../components/Maps/MapsAll';
import TimeSliders from '../../../components/TrackLivestock/TimeSliders';
import { SLIDER_DEFAULT_TIME_FRAME, SLIDER_DEFAULT_TIME_INTERVAL } from '../../../components/TrackLivestock/constants';
import { errorToastHandler } from '../../../components/action_notifier';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

const LivestockTable = lazy(() => import('../../../components/TrackLivestock/LivestockTable'));
const Farms = lazy(() => import('../../../views/pages/farms'));
const Geofences = lazy(() => import('../../../views/pages/geofences'));
const Sites = lazy(() => import('../../../views/pages/sites'));

const { Title } = Typography;

export default function Maps() {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const tab = query.get('tab');

  const { selectedIds, isTimeSlidersVisible, tags } = useSelector(state => state.animal);
  const { permissions } = useSelector(state => state.user);
  const { list: farms } = useSelector(state => state.farm);
  const { data: grazing } = useSelector(state => state.grazing);
  const { myPlan } = useSelector(state => state.subscription);

  const [activeTab, setActiveTab] = useState(tab || DEFAULT_TAB);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();

    return () => {
      resetTimeSlidersData();
      dispatch(setSelectedFilters({}));
    };
  }, [dispatch]);

  useEffect(() => {
    storeMapData(farms);
    getGrazingData();
  }, [farms, tags]);

  async function fetchData() {
    try {
      await Promise.all([
        dispatch(fetchFarms({ with_details: true })),
        dispatch(fetchAnimalsTags({ only_notifications: false })),
        dispatch(fetchGrazingHeatmapStyles())
      ]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  }

  async function getGrazingData() {
    if (!farms.length) return;

    const geofences = farms?.flatMap(g => g.geofences);
    const rotationIds = geofences?.map(g => (g.currentRotation ? g.currentRotation.id : g.lastRotation?.id));

    if (!rotationIds?.length) return;

    try {
      await dispatch(fetchGrazingData({ rotationIds })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  function storeMapData(data) {
    const farms = data?.map(farm => ({ ...farm.master_geofence, name: farm.name }));
    const geofences = data?.flatMap(geo => geo.geofences);
    const sites = data?.flatMap(geo => geo.sites);

    dispatch(
      setMapData({
        tracking:
          !tab || tab === DEFAULT_TAB
            ? {
                ...tags,
                features: tags?.features?.map(f => ({ ...f, properties: { ...f.properties, isVisible: true } }))
              }
            : null,
        farms,
        geofences: geofences?.filter(g => !g.is_master),
        sites,
        selected: []
      })
    );
  }

  const tabItems = useMemo(() => {
    const items = [];

    const hasAdvancedPlan = myPlan?.type === PLAN_TYPE.ADVANCED;

    if (hasPermission([PERMISSIONS.LIVESTOCK_ACTIVITY.VIEW], permissions) && !hasAdvancedPlan) {
      items.push({
        key: MAPS_TAB.LIVESTOCK,
        label: 'Livestock',
        children: (
          <Suspense fallback={<LoadingBar />}>
            <LivestockTable key={activeTab === MAPS_TAB.LIVESTOCK ? uuidv4() : MAPS_TAB.LIVESTOCK} />
          </Suspense>
        )
      });
    }

    if (hasPermission([PERMISSIONS.GEOFENCES.VIEW_OWN], permissions)) {
      items.push({
        key: MAPS_TAB.GEOFENCE,
        label: 'Pastures',
        children: (
          <Suspense fallback={<LoadingBar />}>
            <Geofences key={activeTab === MAPS_TAB.GEOFENCE ? uuidv4() : MAPS_TAB.GEOFENCE} />
          </Suspense>
        )
      });
    }

    if (hasPermission([PERMISSIONS.SITES.VIEW_OWN], permissions)) {
      items.push({
        key: MAPS_TAB.SITE,
        label: 'Site',
        children: (
          <Suspense fallback={<LoadingBar />}>
            <Sites key={activeTab === MAPS_TAB.SITE ? uuidv4() : MAPS_TAB.SITE} />
          </Suspense>
        )
      });
    }

    if (hasPermission([PERMISSIONS.FARMS.VIEW], permissions)) {
      items.push({
        key: MAPS_TAB.FARM,
        label: 'Farm',
        children: (
          <Suspense fallback={<LoadingBar />}>
            <Farms key={activeTab === MAPS_TAB.FARM ? uuidv4() : MAPS_TAB.FARM} />
          </Suspense>
        )
      });
    }

    return items;
  }, [activeTab, permissions, myPlan]);

  const setAnimal = useCallback(id => history.push(`/tracking/${id}`), [history]);

  function handleTimeSlidersChange(value, field) {
    dispatch(setTimeSlidersData({ value, field }));
  }

  function handleTabChange(tab) {
    history.replace(`/maps?tab=${tab}`);
    setActiveTab(tab);
    storeMapData(farms);

    if (tab !== DEFAULT_TAB) resetTimeSlidersData();
  }

  function resetTimeSlidersData() {
    dispatch(setSelectedIds([]));
    dispatch(setTimeSlidersData({ field: 'time_frame', value: SLIDER_DEFAULT_TIME_FRAME }));
    dispatch(setTimeSlidersData({ field: 'time_interval', value: SLIDER_DEFAULT_TIME_INTERVAL }));
    dispatch(setTimeSlidersVisibility(false));
  }

  const breadcrumbItems = [
    {
      title: 'All maps and geo-locations'
    }
  ];

  return (
    <Card>
      <Flex vertical={true}>
        <Title level={4}>Maps</Title>

        <AppBreadcrumb items={breadcrumbItems} />
      </Flex>

      <div style={{ height: '400px' }}>
        <Map loading={loading} grazing={grazing} onAnimalClick={setAnimal} />
      </div>

      {isTimeSlidersVisible && selectedIds?.length > 0 && <TimeSliders sliderHandler={handleTimeSlidersChange} />}

      <Tabs defaultActiveKey={tab || tabItems?.at(0)?.key} items={tabItems} onChange={tab => handleTabChange(tab)} />
    </Card>
  );
}
